import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCcw, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ReprocessSourceItem {
  deviceLastUpdated: string;
  deviceStatus?: string;
  ambientLux?: string;
  onDemandUserId?: string;
  battery?: {
    batterySoC?: string;
    batteryVoltage?: string;
    batteryLevel?: string;
    batteryCharge?: string;
    chargeVin?: string;
  };
  cellular?: {
    cellularStrength?: string;
    cellularQuality?: string;
    cellularRat?: string;
  };
  imageKit?: {
    url?: string;
    imageWatermarkFree?: string;
    watermarkFreeUrl?: string;
  };
  camera?: {
    imageLocation?: string;
    imageToken?: string;
    imageUrl?: string;
  };
}

interface ReprocessImageDialogProps {
  deviceId: string;
  item: ReprocessSourceItem;
  compact?: boolean;
}

/** Default storage bucket used by the ingester. */
const DEFAULT_IMAGE_LOCATION = "savysnap-image-uploads";

/** Reprocessing is switched off until the backend supports it again. */
export const REPROCESS_ENABLED = false;

/** Images can only be reprocessed within this window. */
const REPROCESS_WINDOW_DAYS = 7;

/** False only when the timestamp parses and is older than the window. */
const isWithinReprocessWindow = (timestamp?: string): boolean => {
  if (!timestamp) return true;
  const t = new Date(timestamp).getTime();
  if (isNaN(t)) return true;
  return Date.now() - t <= REPROCESS_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

/** Extract the image filename (token) from the public image URL. */
const deriveImageToken = (url?: string): string => {
  if (!url) return "";
  try {
    const path = new URL(url).pathname.replace(/^\/+/, "");
    return path.split("/").pop() || "";
  } catch {
    const parts = url.split("/");
    return parts[parts.length - 1] || "";
  }
};

type FieldSource = "history" | "device" | "derived" | "default";

const SOURCE_STYLES: Record<FieldSource, string> = {
  history: "border-transparent bg-muted text-muted-foreground",
  device: "border-transparent bg-muted text-muted-foreground",
  derived: "border-transparent bg-muted text-muted-foreground",
  default: "border-destructive/40 bg-destructive/10 text-destructive",
};

interface SourcedValue {
  value: string;
  source: FieldSource;
}

/** A candidate value tagged with where it came from. */
const from = (source: FieldSource, value: unknown) => ({ source, value });

/** First defined, non-empty value coerced to string, else the fallback. */
const pick = (fallback: string, ...values: unknown[]): string => {
  for (const v of values) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return fallback;
};

/** Like pick(), but reports which candidate won (or that the default was used). */
const pickSourced = (
  fallback: string,
  ...candidates: { source: FieldSource; value: unknown }[]
): SourcedValue => {
  for (const c of candidates) {
    if (c.value === undefined || c.value === null) continue;
    const s = String(c.value).trim();
    if (s !== "") return { value: s, source: c.source };
  }
  return { value: fallback, source: "default" };
};

export const ReprocessImageDialog = ({ deviceId, item, compact }: ReprocessImageDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Fields the history API doesn't return, pulled from the device record.
  const { data: deviceRecord } = useQuery({
    queryKey: ["reprocess-device-fields", deviceId],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select("device_firmware, device_status, cellular, gnss, battery, ambient_lux, camera_config")
        .eq("device_id", deviceId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const buildSourcedPayload = (): Record<string, SourcedValue> => {
    const gnss = (deviceRecord?.gnss as Record<string, unknown> | null) || {};
    const deviceCellular = (deviceRecord?.cellular as Record<string, unknown> | null) || {};
    const deviceBattery = (deviceRecord?.battery as Record<string, unknown> | null) || {};
    const cfg = (deviceRecord?.camera_config as Record<string, unknown> | null) || {};

    const cameraSelect = pick("1", cfg.cameraSelect);
    const cameraUsedDerived = cameraSelect === "2" ? "camera2Night" : "camera1Day";

    const imageUrl = pickSourced(
      "",
      from("history", item.camera?.imageUrl),
      from("history", item.imageKit?.url),
    );

    const derivedToken = deriveImageToken(imageUrl.value);

    return {
      deviceID: { value: deviceId, source: "device" },
      deviceStatus: pickSourced(
        "Record",
        from("history", item.deviceStatus),
        from("device", deviceRecord?.device_status),
      ),
      deviceFirmware: pickSourced("5.5", from("device", deviceRecord?.device_firmware)),
      particlePublishedAt: pickSourced("", from("history", item.deviceLastUpdated)),
      deviceSleepTimer: pickSourced("900000", from("device", cfg.withinScheduleSleepTime)),
      batteryVoltage: pickSourced(
        "0",
        from("history", item.battery?.batteryVoltage),
        from("device", deviceBattery.batteryVoltage),
      ),
      batteryLevel: pickSourced(
        "0",
        from("history", item.battery?.batteryLevel),
        from("device", deviceBattery.batteryLevel),
      ),
      batterySoC: pickSourced(
        "0",
        from("history", item.battery?.batterySoC),
        from("device", deviceBattery.batterySoC),
      ),
      batteryCharge: pickSourced(
        "-1",
        from("history", item.battery?.batteryCharge),
        from("device", deviceBattery.batteryCharge),
      ),
      chargeVin: pickSourced(
        "0",
        from("history", item.battery?.chargeVin),
        from("device", deviceBattery.chargeVin),
      ),
      cellularRat: pickSourced(
        "0",
        from("history", item.cellular?.cellularRat),
        from("device", deviceCellular.cellularRat),
      ),
      cellularStrength: pickSourced(
        "0",
        from("history", item.cellular?.cellularStrength),
        from("device", deviceCellular.cellularStrength),
      ),
      cellularQuality: pickSourced(
        "0",
        from("history", item.cellular?.cellularQuality),
        from("device", deviceCellular.cellularQuality),
      ),
      utcTime: pickSourced("", from("device", gnss.utcTime)),
      northSouth: pickSourced("", from("device", gnss.northSouth)),
      latitude: pickSourced("", from("device", gnss.latitude)),
      eastWest: pickSourced("", from("device", gnss.eastWest)),
      longitude: pickSourced("", from("device", gnss.longitude)),
      altitude: pickSourced("0", from("device", gnss.altitude)),
      geoidal: pickSourced("0", from("device", gnss.geoidal)),
      satellites: pickSourced("0", from("device", gnss.satellites)),
      hdop: pickSourced("0", from("device", gnss.hdop)),
      imageToken: pickSourced(
        derivedToken,
        from("history", item.camera?.imageToken),
        from("derived", derivedToken),
      ),
      imageLocation: pickSourced(
        DEFAULT_IMAGE_LOCATION,
        from("history", item.camera?.imageLocation),
      ),
      imageUrl,
      onDemandUserId: pickSourced(
        "NULL",
        from("history", item.onDemandUserId),
        from("device", cfg.onDemandUserId),
      ),
      pcbHardware: pickSourced("v1.0", from("device", cfg.pcbHardware)),
      pcbHwCode: pickSourced("10", from("device", cfg.pcbHwCode)),
      ambientLux: pickSourced(
        "0",
        from("history", item.ambientLux),
        from("device", deviceRecord?.ambient_lux),
      ),
      cameraUsed: pickSourced(
        cameraUsedDerived,
        from("device", cfg.cameraUsed),
        from("derived", cameraUsedDerived),
      ),
    };
  };

  const buildPayload = (): Record<string, string> =>
    Object.fromEntries(
      Object.entries(buildSourcedPayload()).map(([k, v]) => [k, v.value]),
    );

  const handleReprocess = async () => {
    setIsSending(true);
    try {
      const payload = buildPayload();
      const { data, error } = await supabase.functions.invoke("reprocess-device-image", {
        body: payload,
      });
      if (error) throw error;
      if (data && data.success === false) {
        throw new Error(data.error || `Ingester returned ${data.status}`);
      }
      toast.success("Image sent for reprocessing");
      setOpen(false);
    } catch (e) {
      console.error("Reprocess failed:", e);
      toast.error(e instanceof Error ? e.message : "Failed to reprocess image");
    } finally {
      setIsSending(false);
    }
  };

  const preview = open ? buildSourcedPayload() : {};

  if (!REPROCESS_ENABLED) return null;
  if (!isWithinReprocessWindow(item.deviceLastUpdated)) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={compact ? "h-7 gap-1 text-xs" : "gap-2"}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Sparkles className={compact ? "h-3 w-3" : "h-4 w-4"} />
        <RefreshCcw className={compact ? "h-3 w-3" : "h-4 w-4"} />
        Reprocess
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reprocess this image?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This resends the capture to the Snapshot Live content ingester to reprocess the latest AI query.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1 max-h-64 overflow-auto">
            {Object.entries(preview)
              .filter(([key]) => ["deviceID", "deviceStatus", "deviceFirmware"].includes(key))
              .map(([key, entry]) => (
                <div key={key} className="flex gap-2 items-start">
                  <span className="text-muted-foreground shrink-0 w-40">{key}</span>
                  <span className="break-all flex-1">{entry.value}</span>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide border ${SOURCE_STYLES[entry.source]}`}
                  >
                    {entry.source}
                  </span>
                </div>
              ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleReprocess();
              }}
              disabled={isSending}
            >
              {isSending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reprocess
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ReprocessImageDialog;